import { Component } from '@theme/component';
import {
  VariantSelectedEvent,
  VariantUpdateEvent,
  ThemeEvents,
} from "@theme/events";
import { morph } from '@theme/morph';


/**
 * @typedef {{ id: string|number, title: string, available: boolean, options: string[] }} VariantRow
 * @typedef {{ handle: string, id: number, url: string, variants: VariantRow[], options: string[] }} ProductRow
 * @typedef {{ items: ProductRow[] }} SwatchPayload
 */

class VariantMap {
  /**
   * byHandle[handle] = {
   *   url: string,
   *   optionNames: string[], // original names, lowercased
   *   toId:   Record<'size|||width', string>, // (size,width) - id
   *   toOpts: Record<string, {size:string,width:string}> // id - {size,width}
   * }
   * @type {Record<string, { url: string, optionNames: string[], toId: Record<string,string>, toOpts: Record<string, {size:string,width:string,available:boolean}> }>}
   */
  byHandle = {};

  /**
   * Build indexes for all products. Ignores Color (only uses Size + Width).
   * If size/width positions are missing - product is skipped gracefully.
   * @param {SwatchPayload} payload
   */
  build(payload) {
    this.byHandle = {};
    if (!payload || !Array.isArray(payload.items)) return;

    for (const product of payload.items) {
      const names = (product.options || []).map((o) => (o || "").toLowerCase());
      const sizeIdx = names.indexOf("size");
      const widthIdx = names.indexOf("width");

      // If either Size or Width doesn’t exist - skip (only Color present etc.)
      if (sizeIdx === -1 || widthIdx === -1) continue;

      /** @type {Record<string,string>} */
      const toId = {};
      /** @type {Record<string,{size:string,width:string, available: boolean}>} */
      const toOpts = {};

      for (const v of product.variants || []) {
        const opts = v.options || [];
        const size = (opts[sizeIdx] || "").trim();
        const width = (opts[widthIdx] || "").trim();
        if (!size || !width) continue;

        const id = String(v.id);
        toId[VariantMap.key(size, width)] = id;
        toOpts[id] = { size, width, available: !!v.available };
      }

      this.byHandle[product.handle] = {
        url: product.url,
        optionNames: names,
        toId,
        toOpts,
      };
    }
  }

  /**
   * Create from a <script type="application/json"> node.
   * @param {string} selector
   */
  static fromScript(selector) {
    const node = document.querySelector(selector);
    const map = new VariantMap();
    if (!node) return map;
    try {
      map.build(JSON.parse(node.textContent || "{}"));
    } catch (e) {
      console.error("VariantMap: invalid JSON in", selector, e);
    }

    document.addEventListener(
      ThemeEvents.variantUpdate,
      map.onVariantUpdated.bind(map)
    );

    return map;
  }

  /**
   * @param {CustomEvent<{data?: {productHandle?: string, productVariant?: string|number}}>} ev
   */
  onVariantUpdated(ev) {
    const handle = ev?.detail?.data?.productHandle;
    const variantId = ev?.detail?.data?.productVariant;

    if (!handle || !variantId) return;
    this.updateFromHandleAndVariantId(
      String(handle),
      String(variantId),
      ".color-selector__wrapper .color-selector"
    );
  }

  /**
   * Deterministic key for (size,width).
   * @param {string} size
   * @param {string} width
   * @returns {string}
   */
  static key(size, width) {
    return `${size}|||${width}`;
  }

  /**
   * Lookup: (size,width) - variantId (or null).
   * @param {string} handle
   * @param {string} size
   * @param {string} width
   * @returns {string|null}
   */
  getVariantIdByOptions(handle, size, width) {
    const entry = this.byHandle[handle];
    if (!entry || !size || !width) return null;
    return entry.toId[VariantMap.key(size, width)] || null;
  }

  /**
   * Lookup: variantId -> {size,width,available} (or null).
   * @param {string} handle
   * @param {string|number|null} variantId
   * @returns {{size:string,width:string,available:boolean}|null}
   */
  getOptionsByVariantId(handle, variantId) {
    const entry = this.byHandle[handle];
    if (!entry) return null;
    const found = entry.toOpts[String(variantId)];
    return found || null;
  }

  /**
   * Build product URL with optional ?variant=
   * @param {string} handle
   * @param {string|null} variantId
   * @returns {string}
   */
  buildUrl(handle, variantId) {
    const base = this.byHandle[handle]?.url || `/products/${handle}`;
    const u = new URL(base, window.location.origin);
    if (variantId) u.searchParams.set("variant", String(variantId));
    return u.toString();
  }

  /**
   * Update all companion swatch anchors to match (size,width).
   * Each swatch wrapper must have data-product-handle and contain <a>.
   * No-ops if size/width are falsy.
   * @param {string} [wrapperSel]
   */
  updateSwatchAnchors(
    size,
    width,
    wrapperSel = ".color-selector__wrapper .color-selector"
  ) {
    if (!size || !width) return;

    document.querySelectorAll(wrapperSel).forEach((w) => {
      const handle = w.getAttribute("data-product-handle");

      if (!handle) return;
      const vid = this.getVariantIdByOptions(handle, size, width);
      const href = this.buildUrl(handle, vid);

      w.setAttribute("href", href);

      const opts = this.getOptionsByVariantId(handle, vid);
      if (opts && opts.available === false) {
        w.classList.add("variant-unavailable");
      } else {
        w.classList.remove("variant-unavailable");
      }
    });
  }

  /**
   * Main entry: accept current (handle, variantId), resolve Size/Width,
   * then remap all swatch URLs to each handle.
   *
   * - If the product lacks Size/Width (e.g., only Color) - returns false (no updates).
   * - If the specific variant has missing Size/Width - returns false (no updates).
   *
   * @param {string} currentHandle
   * @param {string|number} currentVariantId
   * @param {string} [wrapperSel]
   * @returns {boolean}
   */
  updateFromHandleAndVariantId(
    currentHandle,
    currentVariantId,
    wrapperSel = ".color-selector__wrapper .color-selector"
  ) {
    const opts = this.getOptionsByVariantId(currentHandle, currentVariantId);

    if (!opts || !opts.size || !opts.width) {
      return false;
    }

    this.updateSwatchAnchors(opts.size, opts.width, wrapperSel);
    return true;
  }
}


/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #requestSequence = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#syncImplicitSizeOption();
    this.threshold = document.querySelector('[data-inventory-threshold]');

    if (this.threshold) {
      this.thresholdVal = Number(this.threshold.dataset.inventoryThreshold);
      this.note = document.querySelector('.limited-qty-note');
      this.availability = this._readAvailability();
      this.addEventListener('change', () => this._toggleNote());
      document.addEventListener('variant:change', () => this._toggleNote());
      this._toggleNote();
    }

    this.addEventListener('change', this.variantChanged.bind(this));

    this._onSwatchClick =
      this._onSwatchClick?.bind(this) || this._onSwatchClickBind();
    this.addEventListener('click', this._onSwatchClick, { capture: false });
  }

  #syncImplicitSizeOption() {
    if (this.dataset.templateProductMatch !== 'true') return;
    if (!this.querySelector('[data-implicit-option-name="size"]')) return;

    const productSection = this.closest('#product-information-section');
    if (productSection instanceof HTMLElement) {
      productSection.dataset.sizeSelected = 'true';
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onSwatchClick);
    super.disconnectedCallback?.();
  }

  /**
   * Creates and returns a click handler for variant swatches.
   * Intercepts clicks on <a.color-selector> elements, prevents navigation hystory
   *
   * @returns {(e: MouseEvent) => void} A bound click handler function.
   */
  _onSwatchClickBind() {
    /**
     * @param {MouseEvent} e - The click event object.
     * @returns {void}
     */
    return (e) => {
      /** @type {HTMLAnchorElement | null} */
      const link = /** @type {HTMLElement} */ (e.target)?.closest(
        'a.color-selector'
      );

      if (!link) return;

      if (
        link.getAttribute('aria-disabled') === 'true' ||
        link.classList.contains('is-disabled') ||
        link.classList.contains('variant-unavailable')
      ) {
        e.preventDefault();
        return;
      }

      const rawHref = link.getAttribute('href');
      if (!rawHref || rawHref === '#' || rawHref.startsWith('#')) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      location.replace(link.href);
    };
  }

  _readAvailability() {
    const el = document.querySelector('script[data-variant-availability]');
    return el ? JSON.parse(el.textContent) : null;
  }

  _getCurrentVariant() {
    if (!this.availability) return null;

    const optionNames = this.availability.options;
    const values = optionNames.map((name) => {
      if (/color/i.test(name)) return null;
      const input = this.querySelector(
        `[name="options[${name}]"]:checked, select[name="options[${name}]"]`
      );
      return input ? input.value : null;
    });

    return this.availability.variants.find(v => {
      return v.options.every((opt, i) => {
        if (/color/i.test(optionNames[i])) return true;
        return String(opt) === String(values[i]);
      });
    }) || null;
}


  _toggleNote() {
    if (!this.note) return;
    const variant = this._getCurrentVariant();
    if (!variant) return;

    const show = variant.available && variant.inventory > 0 && variant.inventory <= this.thresholdVal;

    this.note.hidden = !show;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product.digital-gift-card' ||
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    const requestId = ++this.#requestSequence;
    this.#setVariantLoadingState(true, requestId);

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const selectedVariant = html.querySelector(`variant-picker #data-selected-variant`)?.textContent;
        const product = html.querySelector(`#data-product-json`)?.textContent;

        if (!selectedVariant || !product) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(
                JSON.parse(selectedVariant),
                this.selectedOptionId,
                {
                  html,
                  productId: this.dataset.productId ?? '',
                  productHandle: this.dataset.productHandle ?? '',
                  productVariant: JSON.parse(selectedVariant).id,
                  newProduct,
                }
              )
            );

             window.ShopAnalytics?.events.viewItem({
              product: JSON.parse(product),
              variant: JSON.parse(selectedVariant),
            }); 
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        this.#setVariantLoadingState(false, requestId);
      });
  }

  /**
   * Broadcasts loading state while a variant update request is in-flight.
   * Uses request sequence guard to prevent stale requests from unlocking UI.
   *
   * @param {boolean} isLoading
   * @param {number} requestId
   */
  #setVariantLoadingState(isLoading, requestId) {
    if (!isLoading && requestId !== this.#requestSequence) return;

    this.dataset.variantLoading = String(isLoading);
    this.dispatchEvent(
      new CustomEvent('variant:loading', {
        bubbles: true,
        detail: { isLoading },
      })
    );
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

if (!customElements.get('variant-dropdown')) {
  const GLOBAL_PDP_SIZE_GRID_ROWS = [
    { us: "6", eu: "39", uk: "5" },
    { us: "6.5", eu: "39.5", uk: "5.5" },
    { us: "7", eu: "40", uk: "6" },
    { us: "7.5", eu: "40.5", uk: "6.5" },
    { us: "8", eu: "41", uk: "7" },
    { us: "8.5", eu: "41.5", uk: "7.5" },
    { us: "9", eu: "42", uk: "8" },
    { us: "9.5", eu: "42.5", uk: "8.5" },
    { us: "10", eu: "43", uk: "9" },
    { us: "10.5", eu: "43.5", uk: "9.5" },
    { us: "11", eu: "44", uk: "10" },
    { us: "11.5", eu: "44.5", uk: "10.5" },
    { us: "12", eu: "45", uk: "11" },
    { us: "12.5", eu: "45.5", uk: "11.5" },
    { us: "13", eu: "46", uk: "12" },
    { us: "14", eu: "47", uk: "13" },
    { us: "15", eu: "48", uk: "14" },
    { us: "16", eu: "49", uk: "15" },
  ];

  const normalizeSizeGridValue = (value) =>
    String(value ?? "").trim().replace(/\s+/g, "");

  const getLocalizedSizeLabel = (value, sourceGrid, targetGrid) => {
    const normalizedValue = normalizeSizeGridValue(value);

    if (!normalizedValue || !sourceGrid || !targetGrid) {
      return String(value ?? "").trim();
    }

    const matchedRow = GLOBAL_PDP_SIZE_GRID_ROWS.find(
      (row) => normalizeSizeGridValue(row[sourceGrid]) === normalizedValue
    );

    return matchedRow?.[targetGrid] || String(value ?? "").trim();
  };

  customElements.define(
    "variant-dropdown",
    class VariantDropdown extends HTMLElement {
      /** @type {HTMLDivElement | null} */
      __overlay = null;

      constructor() {
        super();
        this.elements = {
          input: this.querySelector(".variant-dropdown__input"),
          button: this.querySelector(".variant-dropdown__select"),
          panel: this.querySelector(".disclosure__list-wrapper"),
          listbox: this.querySelector('[role="listbox"]'),
          liveSel: this.querySelector(".variant-dropdown__selected-label"),
          radios: Array.from(
            this.querySelectorAll('fieldset input[type="radio"]')
          ),
          options: Array.from(this.querySelectorAll('[role="option"]')),
          productSection: document.getElementById(
            "product-information-section"
          ),
          sizeField: document.querySelector(
            '.variant-dropdown[data-option-name="size"]'
          ),
          sizeGridButtons: Array.from(
            this.querySelectorAll("[data-size-grid-toggle]")
          ),
        };
        this.map = VariantMap.fromScript("#swatch-products-json");
        this.body = document.querySelector("body");

        const listId = this._ensureId(this.elements.listbox, "vd-list");
        if (this.elements.button) {
          this.elements.button.setAttribute("aria-controls", listId);
          this.elements.button.setAttribute("aria-haspopup", "listbox");
        }
        if (this.elements.listbox) {
          this.elements.listbox.setAttribute("tabindex", "-1");
        }

        this.activeIndex = Math.max(
          0,
          this.elements.options.findIndex(
            (o) => o.getAttribute("aria-selected") === "true"
          )
        );

        this._checkSizeSelectionState();
        this._checkGiftSelectionState();

        this.addEventListener("keydown", this.onContainerKeyDown.bind(this));
        this.addEventListener("keyup", this.onContainerKeyUp.bind(this));
        this.addEventListener("focusout", this.onFocusOut.bind(this));
        this.addEventListener("click", this._onSizeGridToggle.bind(this));

        this._renderSizeGrid();

        if (this._isGlobalSizeGridEnabled()) {
          this.addEventListener('sizeGridChange', (e) => {
            this.dataset.activeSizeGrid = e.detail.grid;
            this._renderSizeGrid();
          });
        }

        if (this.elements.button) {
          this.elements.button.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.togglePanel();
          });
        }

        this.addEventListener("click", (e) => {
          const opt = e.target?.closest?.('[role="option"]');
          if (!opt || !this.contains(opt)) return;
          e.preventDefault();

          this._refreshElements();
          this.onOptionClick(opt);
        });

        document.addEventListener("click", (e) => {
          if (!(e.target instanceof Node) || !this.contains(e.target))
            this.hidePanel();
        });

        const selectedOpt =
          this.elements.options[this.activeIndex] || this.elements.options[0];
        if (selectedOpt) {
          this._applySelection(selectedOpt, false);
        }

        this.__overlay = document.createElement("div");
        this.__overlay.className = "variant-dropdown__overlay";
        document.body.appendChild(this.__overlay);
        this.__overlay.addEventListener("click", () => this.hidePanel());

        this.addEventListener("click", (e) => {
          const btn = e.target.closest(".variant-dropdown__sheet-close");
          if (btn && this.contains(btn)) {
            e.preventDefault();
            this.hidePanel();
          }
        });
      }

      disconnectedCallback() {
        this.__overlay?.remove();
        this.__overlay = null;
      }

      openPanel() {
        this._refreshElements();
        this.elements.panel?.removeAttribute("hidden");
        this.elements.button?.setAttribute("aria-expanded", "true");
        this.classList.add("is-open");

        this.__overlay?.classList.add("is-visible");

        document.body.classList.add("no-scroll");

        this._focusActive?.();
      }

      hidePanel() {
        this.elements.button?.setAttribute("aria-expanded", "false");
        this.classList.remove("is-open");
        this.__overlay?.classList.remove("is-visible");

        const panel = this.elements.panel;
        if (panel && window.matchMedia("(max-width: 749px)").matches) {
          const onEnd = () => {
            panel.setAttribute("hidden", "");
            panel.removeEventListener("transitionend", onEnd);
          };
          panel.addEventListener("transitionend", onEnd);
        } else {
          panel?.setAttribute("hidden", "");
        }

        document.body.classList.remove("no-scroll");

        this.elements.listbox?.removeAttribute("aria-activedescendant");
      }

      togglePanel() {
        this._refreshElements();
        const isOpen =
          this.elements.button &&
          this.elements.button.getAttribute("aria-expanded") === "true";
        if (isOpen) {
          this.hidePanel();
          if (this.elements.button) {
            this.elements.button instanceof HTMLElement &&
              this.elements.button.focus();
          }
        } else {
          this.openPanel();
        }
      }


      onContainerKeyUp(event /** @type {KeyboardEvent} */) {
        switch (event.code.toUpperCase()) {
          case "ESCAPE":
            if (
              this.elements.button &&
              this.elements.button.getAttribute("aria-expanded") === "true"
            ) {
              this.hidePanel();
              event.stopPropagation();
              this.elements.button instanceof HTMLElement &&
                this.elements.button.focus();
            }
            break;
          case "SPACE":
            if (
              document.activeElement === this.elements.button &&
              this.elements.button instanceof HTMLElement &&
              this.elements.button.getAttribute("aria-expanded") === "false"
            ) {
              event.preventDefault();
              this.openPanel();
            }
            break;
        }
      }

      onContainerKeyDown(event /** @type {KeyboardEvent} */) {
        const isOpen =
          this.elements.button instanceof HTMLElement &&
          this.elements.button.getAttribute("aria-expanded") === "true";
        const k = event.code.toUpperCase();

        if (!isOpen && (k === "ARROWDOWN" || k === "ARROWUP")) {
          event.preventDefault();
          this.openPanel();
          return;
        }

        if (!isOpen) return;

        switch (k) {
          case "ARROWDOWN":
            event.preventDefault();
            this._moveActive(1);
            break;
          case "ARROWUP":
            event.preventDefault();
            this._moveActive(-1);
            break;
          case "HOME":
            event.preventDefault();
            this._setActive(0);
            break;
          case "END":
            event.preventDefault();
            this._setActive(this.elements.options.length - 1);
            break;
          case "ENTER":
          case "SPACE": {
            event.preventDefault();
            const opt = this.elements.options[this.activeIndex];
            if (opt) {
              this._handleSizeSelection(opt);
              this._handleGiftSelection(opt);
              this._applySelection(opt, true);
              this.hidePanel();
              this.elements.button instanceof HTMLElement &&
                this.elements.button.focus();
            }
            break;
          }
        }
      }

      onFocusOut(event) {
        const next = event.relatedTarget;
        if (!next) return;
        if (!this.contains(next)) this.hidePanel();
      }

      /**
       * @param {HTMLElement} opt - The submit event.
       */
      onOptionClick(opt) {
        this._handleSizeSelection(opt);
        this._handleGiftSelection(opt);
        this._applySelection(opt, true);
        this.hidePanel();
        this.elements.button instanceof HTMLElement &&
          this.elements.button.focus();
      }

      /**
       * @param {Number|String} currentVariantId - The submit event.
       */
      _updateSwatchAnchors(currentVariantId) {
        const currentHandle = this.dataset.productHandle;

        if (currentHandle && currentVariantId) {
          this.map.updateFromHandleAndVariantId(
            currentHandle,
            currentVariantId
          );
        }
      }

      _findMatchingRadio(value) {
        const rByIndex = this.elements.radios[this.activeIndex];
        if (rByIndex && rByIndex.value === value) return rByIndex;
        return this.elements.radios.find((r) => r.value === value) || null;
      }

      /**
       * Handles the click on size option
       *
       * @param {HTMLElement} opt - The submit event.
       */
      _handleSizeSelection(opt) {
        const productSection = this._getProductSection();

        if (
          opt.dataset.optionName === "size" &&
          productSection?.dataset.sizeSelected !== "true"
        ) {
          this._setSizeSelection(true);
          this._removeValidationError();

          // To bind swatch url update when selected default option (fake click)
          const variantId = opt?.dataset?.variantId;
          if (variantId) this._updateSwatchAnchors(variantId);
        }
      }

      /**
       * Handles the click on gift option
       *
       * @param {HTMLElement} opt - The submit event.
       */
      _handleGiftSelection(opt) {
        const productSection = this._getProductSection();

        if (
          opt.dataset.optionName === "denomination" &&
          productSection?.dataset.giftSelected !== "true"
        ) {
          this._setGiftSelection(true);
          this._removeValidationError();

        }
      }

      /**
       * Handles the submit event for the product form.
       *
       * @param {boolean} state - The submit event.
       */
      _setSizeSelection(state) {
        const productSection = this._getProductSection();

        if (productSection) {
          productSection.setAttribute(
            "data-size-selected",
            `${state}`
          );
        }
      }

      _setGiftSelection(state) {
        const productSection = this._getProductSection();

        if (productSection) {
          productSection.setAttribute(
            "data-gift-selected",
            `${state}`
          );
        }
      }

      _removeValidationError() {
        const sizeField = this._getSizeField();

        if (sizeField) {
          sizeField.classList.remove("validation-error");
        }
      }

      _onSizeGridToggle(event) {
        const button = event.target?.closest?.("[data-size-grid-toggle]");

        if (!button || !this.contains(button) || !this._isGlobalSizeGridEnabled()) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.dataset.activeSizeGrid =
          button.dataset.sizeGridToggle || this.dataset.activeSizeGrid;
        this._renderSizeGrid();

        // Set the selected grid in localStorage for persistence
        localStorage.setItem('selectedSizeGrid', this.dataset.activeSizeGrid);
      }

      // Always set true if variant id is present in url
      _checkSizeSelectionState() {
        if (this.dataset.optionName !== "size") return;

        if (this.dataset.implicitOptionName === "size") {
          this._setSizeSelection(true);
          return;
        }

        const url = new URL(window.location.href);
        const variantId = url.searchParams.get("variant");

        if (variantId) {
          this._setSizeSelection(true);
          // If variant is present update all swatches urls
          this._updateSwatchAnchors(variantId);
        } else {
          this._setSizeSelection(false);
        }
      }

       // Always set true if variant id is present in url
      _checkGiftSelectionState() {
        const url = new URL(window.location.href);
        const variantId = url.searchParams.get("variant");

        if (variantId) {
          this._setGiftSelection(true);
          // If variant is present update all swatches urls
          //this._updateSwatchAnchors(variantId);
        } else {
          this._setGiftSelection(false);
        }
      }

      _isGlobalSizeGridEnabled() {
        return (
          this.dataset.optionName === "size" &&
          this.dataset.globalSizeGridEnabled === "true"
        );
      }

      _renderSizeGrid() {
        this._refreshElements();

        if (!this._isGlobalSizeGridEnabled()) {
          return;
        }

        const sourceGrid = (this.dataset.defaultSizeGrid || "us").toLowerCase();
        const activeSizeGrid = (this.dataset.activeSizeGrid || sourceGrid).toLowerCase();

        this.elements.sizeGridButtons.forEach((button) => {
          const isActive = button.dataset.sizeGridToggle === activeSizeGrid;
          button.classList.toggle("is-active", isActive);
          button.setAttribute("aria-pressed", String(isActive));
        });

        this.elements.options.forEach((option) => {
          const displayValue = getLocalizedSizeLabel(
            option.dataset.value,
            sourceGrid,
            activeSizeGrid
          );
          option.dataset.text = displayValue;

          const optionLabel = option.querySelector(".variant-dropdown__option-label");
          if (optionLabel) {
            optionLabel.textContent = displayValue;
          }
        });

        const selectedOption =
          this.elements.options.find(
            (option) => option.getAttribute("aria-selected") === "true"
          ) || this.elements.options[this.activeIndex];

        if (selectedOption && this.elements.liveSel) {
          this.elements.liveSel.textContent =
            selectedOption.dataset.text || selectedOption.dataset.value || "";
        }
      }

      _applySelection(opt, fire) {
        this._refreshElements();
        this.elements.options.forEach((o) =>
          o.setAttribute("aria-selected", "false")
        );
        opt.setAttribute("aria-selected", "true");
        this.activeIndex = this.elements.options.indexOf(opt);

        const text =
          opt.dataset.text || opt.textContent.trim() || opt.dataset.value;
        const value = opt.dataset.value;
        if (this.elements.liveSel) this.elements.liveSel.textContent = text;
        if (this.elements.input) this.elements.input.value = value;

        const radio = this._findMatchingRadio(value);
        if (radio instanceof HTMLInputElement && !radio.checked) {
          radio.checked = true;

          if (fire) {
            radio.dispatchEvent(
              new Event("input", { bubbles: true, composed: true })
            );
            radio.dispatchEvent(
              new Event("change", { bubbles: true, composed: true })
            );
          }
        }

        if (fire) {
          this.elements.input?.dispatchEvent(
            new Event("change", { bubbles: true })
          );
          this.dispatchEvent(
            new CustomEvent("optionValueSelectionChange", {
              bubbles: true,
              detail: {
                optionPosition: Number(this.dataset.optionPosition || 1),
                optionName: this.dataset.optionName || "",
                value,
                optionValueId: opt.dataset.optionValueId || null,
                variantId: opt.dataset.variantId || null,
              },
            })
          );
        }
      }

      _moveActive(step) {
        this._refreshElements();
        const total = this.elements.options.length;
        let next = this.activeIndex + step;
        if (next < 0) next = 0;
        if (next > total - 1) next = total - 1;
        this._setActive(next);
      }

      _setActive(index) {
        this._refreshElements();
        this.activeIndex = index;
        const opt = this.elements.options[this.activeIndex];
        if (!opt) return;
        this.elements.listbox.setAttribute(
          "aria-activedescendant",
          opt.id || this._ensureId(opt, "vd-opt")
        );
        this._scrollOptionIntoView(opt);
        opt.focus({ preventScroll: true });
      }

      _focusActive() {
        this._refreshElements();
        const opt =
          this.elements.options[this.activeIndex] || this.elements.options[0];
        if (opt) {
          this.elements.listbox.setAttribute(
            "aria-activedescendant",
            opt.id || this._ensureId(opt, "vd-opt")
          );
          this.elements.listbox.focus();
          this._scrollOptionIntoView(opt);
        }
      }

      _scrollOptionIntoView(opt) {
        const c = this.elements.listbox;
        const oTop = opt.offsetTop;
        const oBottom = oTop + opt.offsetHeight;
        const vTop = c.scrollTop;
        const vBottom = vTop + c.clientHeight;
        if (oTop < vTop) c.scrollTop = oTop;
        else if (oBottom > vBottom) c.scrollTop = oBottom - c.clientHeight;
      }

      _ensureId(el, prefix) {
        if (!el.id)
          el.id = `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
        return el.id;
      }

      _getProductSection() {
        if (
          !this.elements.productSection ||
          !document.contains(this.elements.productSection)
        ) {
          this.elements.productSection = document.getElementById(
            "product-information-section"
          );
        }

        return this.elements.productSection;
      }

      _getSizeField() {
        if (!this.elements.sizeField || !document.contains(this.elements.sizeField)) {
          this.elements.sizeField = document.querySelector(
            '.variant-dropdown[data-option-name="size"]'
          );
        }

        return this.elements.sizeField;
      }

      _refreshElements() {
        this.elements.input = this.querySelector(".variant-dropdown__input");
        this.elements.button = this.querySelector(".variant-dropdown__select");
        this.elements.panel = this.querySelector(".disclosure__list-wrapper");
        this.elements.listbox = this.querySelector('[role="listbox"]');
        this.elements.liveSel = this.querySelector(".variant-dropdown__selected-label");
        this.elements.radios = Array.from(
          this.querySelectorAll('fieldset input[type="radio"]')
        );
        this.elements.options = Array.from(this.querySelectorAll('[role="option"]'));
        this.elements.sizeGridButtons = Array.from(
          this.querySelectorAll("[data-size-grid-toggle]")
        );
        this._getProductSection();
        this._getSizeField();

        if (this.activeIndex >= this.elements.options.length) {
          this.activeIndex = Math.max(0, this.elements.options.length - 1);
        }
      }
    }
  );
}
